import { Loader2, Mail, CheckCircle, Clock, UserX, Send } from 'lucide-react';
import { useClientPortalStatusQuery, useResendClientInviteMutation } from '../../services/hooks/useClientes';
import type { PortalAccountStatus as Status } from '../../services/api/clienteService';
import { formatDate } from '../../utils/utils';

interface PortalAccountStatusProps {
  clientId: string;
}

const STATUS_META: Record<Status, { label: string; className: string; icon: typeof CheckCircle }> = {
  active: { label: 'Cuenta activa', className: 'bg-green-100 text-green-700', icon: CheckCircle },
  registered: { label: 'Registrado (sin entrar al portal)', className: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  invited: { label: 'Invitación enviada', className: 'bg-yellow-100 text-yellow-700', icon: Clock },
  not_invited: { label: 'Sin invitar', className: 'bg-gray-100 text-gray-600', icon: Mail },
  no_email: { label: 'Sin email', className: 'bg-gray-100 text-gray-500', icon: UserX },
  unknown: { label: 'Desconocido', className: 'bg-gray-100 text-gray-500', icon: UserX },
};

// Resending only makes sense when there is no account yet but we do have an email.
const CAN_RESEND: Status[] = ['invited', 'not_invited'];

const PortalAccountStatus = ({ clientId }: PortalAccountStatusProps) => {
  const { data, isLoading, isError } = useClientPortalStatusQuery(clientId);
  const resendInvite = useResendClientInviteMutation();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 size={16} className="animate-spin" /> Comprobando acceso al portal...
      </div>
    );
  }

  if (isError || !data) {
    return <p className="text-sm text-red-500">No se pudo comprobar el acceso al portal.</p>;
  }

  const meta = STATUS_META[data.status] ?? STATUS_META.unknown;
  const Icon = meta.icon;
  const canResend = CAN_RESEND.includes(data.status);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Acceso al portal</p>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.className}`}>
          <Icon size={14} />
          {meta.label}
          {data.status === 'invited' && data.invitedAt ? ` el ${formatDate(new Date(data.invitedAt))}` : ''}
        </span>
      </div>

      {canResend && (
        <button
          onClick={() => resendInvite.mutate(clientId)}
          disabled={resendInvite.isPending}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white transition-colors disabled:opacity-50"
        >
          {resendInvite.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {data.status === 'invited' ? 'Reenviar invitación' : 'Enviar invitación'}
        </button>
      )}

      {data.status === 'no_email' && (
        <span className="text-xs text-gray-400">Añade un email para poder invitar al cliente.</span>
      )}
    </div>
  );
};

export default PortalAccountStatus;
